/**
 * Create / edit dialog for a local user account (`POST` and `PATCH
 * /api/admin/users`).
 *
 * Two things make this more than a form:
 *
 *   1. PATCH takes a SPARSE body (`UpdateUserRequest` is the only request type
 *      with optional members) and the backend answers 422 "Keine Änderungen
 *      übermittelt." for an empty one. The edit mode therefore diffs against the
 *      loaded user and sends only what actually changed — and refuses to submit
 *      when nothing did, instead of provoking that 422.
 *   2. Every server message is German already (409 duplicate e-mail, 409 last
 *      active admin, 422 password rules) and is rendered VERBATIM in the inline
 *      banner. The client-side length check below only mirrors the cheapest of
 *      the server's rules so the user gets instant feedback; the server stays
 *      authoritative (it also enforces a deny-list this form knows nothing
 *      about).
 */

import { Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import { Modal } from '../../components/Modal';
import Spinner from '../../components/Spinner';
import { createAdminUser, errorMessage, updateAdminUser } from '../../lib/api';
import { ROLE_LABELS } from '../../lib/types';
import type {
  AdminUser,
  CreateUserRequest,
  Role,
  RoleInfo,
  UpdateUserRequest,
} from '../../lib/types';

/**
 * Mirrors `MIN_PASSWORD_LENGTH` in app/auth/security.py — used for the hint and
 * a pre-submit check only. The server's German 422 remains authoritative.
 */
const MIN_PASSWORD_LENGTH = 12;

/** Unambiguous alphabet: no O/0, l/1/I — generated passwords get read aloud. */
const PASSWORD_ALPHABET = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789-_';

/** Cryptographically random password for the "Passwort erzeugen" button. */
function generatePassword(length = 20): string {
  const values = new Uint32Array(length);
  crypto.getRandomValues(values);
  let out = '';
  for (const value of values) {
    out += PASSWORD_ALPHABET[value % PASSWORD_ALPHABET.length] ?? '';
  }
  return out;
}

function sameRoleSet(a: readonly Role[], b: readonly Role[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set<Role>(a);
  return b.every((role) => set.has(role));
}

function roleLabel(role: Role | string): string {
  const labels: Readonly<Record<string, string | undefined>> = ROLE_LABELS;
  return labels[role] ?? role;
}

export interface UserFormModalProps {
  /** 'create' opens an empty form, 'edit' pre-fills from `user`. */
  mode: 'create' | 'edit';
  /** The row being edited; `null` in create mode. */
  user: AdminUser | null;
  /** Selectable roles — from `GET /api/admin/roles`, server-authoritative. */
  availableRoles: readonly RoleInfo[];
  /** True when the edited row is the signed-in account (extra warnings). */
  isSelf: boolean;
  onClose: () => void;
  /** Called with the server's row and a translated success message. */
  onSaved: (user: AdminUser, message: string) => void;
}

export function UserFormModal({
  mode,
  user,
  availableRoles,
  isSelf,
  onClose,
  onSaved,
}: UserFormModalProps): ReactNode {
  const { t } = useLingui();
  const creating = mode === 'create';

  const [email, setEmail] = useState(user?.email ?? '');
  const [name, setName] = useState(user?.name ?? '');
  const [roles, setRoles] = useState<readonly Role[]>(user?.roles ?? []);
  const [active, setActive] = useState(user?.active ?? true);
  const [resetPassword, setResetPassword] = useState(creating);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forceChange, setForceChange] = useState(user?.must_change_password ?? false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const formId = creating ? 'adm-user-create' : 'adm-user-edit';
  const passwordActive = creating || resetPassword;
  const tooShort = passwordActive && password !== '' && password.length < MIN_PASSWORD_LENGTH;
  const emailInvalid = creating && email.trim() !== '' && !email.includes('@');

  const toggleRole = useCallback((role: Role, on: boolean): void => {
    setRoles((current) => {
      if (on) return current.includes(role) ? current : [...current, role];
      return current.filter((candidate) => candidate !== role);
    });
  }, []);

  /** The sparse PATCH body — empty when the form still matches the server row. */
  const changes = useMemo<UpdateUserRequest>(() => {
    if (creating || user === null) return {};
    const next: UpdateUserRequest = {};
    if (name.trim() !== (user.name ?? '')) next.name = name.trim();
    if (!sameRoleSet(roles, user.roles)) next.roles = [...roles];
    if (active !== user.active) next.active = active;
    if (resetPassword && password !== '') next.password = password;
    if (forceChange !== user.must_change_password) next.must_change_password = forceChange;
    return next;
  }, [creating, user, name, roles, active, resetPassword, password, forceChange]);

  const hasChanges = Object.keys(changes).length > 0;

  const canSubmit = creating
    ? !saving &&
      email.trim() !== '' &&
      !emailInvalid &&
      password.length >= MIN_PASSWORD_LENGTH
    : !saving && hasChanges && (!resetPassword || password.length >= MIN_PASSWORD_LENGTH);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      if (creating) {
        const body: CreateUserRequest = {
          email: email.trim(),
          name: name.trim(),
          password,
          roles: [...roles],
          active,
        };
        const response = await createAdminUser(body);
        const savedEmail = response.user.email;
        onSaved(response.user, t`Benutzer ${savedEmail} wurde angelegt.`);
      } else if (user !== null) {
        const response = await updateAdminUser(user.id, changes);
        const savedEmail = response.user.email;
        onSaved(response.user, t`Benutzer ${savedEmail} wurde geändert.`);
      }
    } catch (err) {
      // German server detail verbatim: 409 duplicate e-mail, 409 last active
      // admin, 422 password rules / "Keine Änderungen übermittelt.".
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={creating ? t`Benutzer anlegen` : t`Benutzer bearbeiten`}
      subtitle={creating ? undefined : user?.email}
      onClose={onClose}
      dismissable={!saving}
      footer={
        <>
          <button type="submit" form={formId} className="primary" disabled={!canSubmit}>
            {saving ? (
              <Spinner variant="on-accent" label={t`Wird gespeichert…`} />
            ) : creating ? (
              <Trans>Anlegen</Trans>
            ) : (
              <Trans>Änderungen speichern</Trans>
            )}
          </button>
          <button type="button" className="ghost" onClick={onClose} disabled={saving}>
            <Trans>Abbrechen</Trans>
          </button>
          {!creating && !hasChanges ? (
            <span className="muted" style={{ fontSize: 'var(--text-sm)' }}>
              <Trans>Keine Änderungen</Trans>
            </span>
          ) : null}
        </>
      }
    >
      <form id={formId} className="stack" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor={`${formId}-email`}><Trans>E-Mail (Login)</Trans></label>
          <input
            id={`${formId}-email`}
            type="email"
            autoComplete="off"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-invalid={emailInvalid}
            disabled={saving || !creating}
            required={creating}
          />
          {creating ? (
            <span className="hint">
              <Trans>Wird klein geschrieben gespeichert und ist der Anmeldename.</Trans>
            </span>
          ) : (
            <span className="hint">
              <Trans>Die E-Mail-Adresse kann nachträglich nicht geändert werden.</Trans>
            </span>
          )}
          {emailInvalid ? (
            <span className="err"><Trans>Bitte eine gültige E-Mail-Adresse angeben.</Trans></span>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor={`${formId}-name`}><Trans>Name</Trans></label>
          <input
            id={`${formId}-name`}
            type="text"
            autoComplete="off"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={saving}
            placeholder={t`Anzeigename (optional)`}
          />
        </div>

        <div className="field">
          <span className="field-label"><Trans>Rollen</Trans></span>
          <div className="adm-roles">
            {availableRoles.length === 0 ? (
              <span className="hint"><Trans>Keine Rollen verfügbar.</Trans></span>
            ) : (
              availableRoles.map((info) => {
                const on = roles.includes(info.name);
                return (
                  <label key={info.name} className={on ? 'adm-role on' : 'adm-role'}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(event) => toggleRole(info.name, event.target.checked)}
                      disabled={saving}
                    />
                    <span className="adm-role-text">
                      <span className="adm-role-name">{roleLabel(info.name)}</span>
                      <span className="adm-role-desc">
                        {info.description !== '' ? info.description : info.permissions.join(', ')}
                      </span>
                    </span>
                  </label>
                );
              })
            )}
          </div>
          {roles.length === 0 ? (
            <span className="hint">
              <Trans>Ohne Rolle kann sich das Konto anmelden, sieht aber keine Inhalte.</Trans>
            </span>
          ) : null}
          {isSelf && !roles.includes('admin') && (user?.roles ?? []).includes('admin') ? (
            <span className="err">
              <Trans>
                Sie entziehen sich selbst die Administrator-Rolle. Danach ist die
                Benutzerverwaltung für Sie nicht mehr erreichbar.
              </Trans>
            </span>
          ) : null}
        </div>

        <label className="check">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
            disabled={saving}
          />
          <Trans>Aktiv (Anmeldung erlaubt)</Trans>
        </label>

        {creating ? null : (
          <label className="check">
            <input
              type="checkbox"
              checked={resetPassword}
              onChange={(event) => {
                const on = event.target.checked;
                setResetPassword(on);
                if (!on) setPassword('');
                // A password handed over by an administrator should be replaced
                // by the user on first login.
                if (on) setForceChange(true);
              }}
              disabled={saving}
            />
            <Trans>Passwort zurücksetzen</Trans>
          </label>
        )}

        {passwordActive ? (
          <div className="field">
            <label htmlFor={`${formId}-password`}>
              {creating ? t`Initiales Passwort` : t`Neues Passwort`}
            </label>
            <div className="row" style={{ gap: 'var(--space-2)' }}>
              <input
                id={`${formId}-password`}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-invalid={tooShort}
                aria-describedby={`${formId}-password-hint`}
                disabled={saving}
                required={creating}
                style={{ flex: '1 1 220px' }}
              />
              <button
                type="button"
                className="ghost small"
                onClick={() => setShowPassword((value) => !value)}
                disabled={saving}
              >
                {showPassword ? t`Verbergen` : t`Anzeigen`}
              </button>
              <button
                type="button"
                className="ghost small"
                onClick={() => {
                  setPassword(generatePassword());
                  setShowPassword(true);
                }}
                disabled={saving}
              >
                <Trans>Erzeugen</Trans>
              </button>
            </div>
            <span className="hint" id={`${formId}-password-hint`}>
              <Trans>
                Mindestens {MIN_PASSWORD_LENGTH} Zeichen; leicht erratbare Passwörter werden
                abgewiesen.
              </Trans>
            </span>
            {tooShort ? (
              <span className="err">
                <Trans>Passwort muss mindestens {MIN_PASSWORD_LENGTH} Zeichen lang sein.</Trans>
              </span>
            ) : null}
            {creating ? (
              <span className="hint">
                <Trans>
                  Einen Passwortwechsel bei der nächsten Anmeldung können Sie nach dem Anlegen unter
                  „Bearbeiten“ erzwingen.
                </Trans>
              </span>
            ) : null}
          </div>
        ) : null}

        {/*
          `must_change_password` exists only on PATCH (UpdateUserRequest), not on
          POST — so this control belongs to the edit dialog.
        */}
        {creating ? null : (
          <label className="check">
            <input
              type="checkbox"
              checked={forceChange}
              onChange={(event) => setForceChange(event.target.checked)}
              disabled={saving}
            />
            <Trans>Passwortwechsel bei der nächsten Anmeldung erzwingen</Trans>
          </label>
        )}

        {error !== null ? <Banner kind="error">{error}</Banner> : null}
      </form>
    </Modal>
  );
}

export default UserFormModal;
